import { Component, inject, OnInit, signal } from '@angular/core';
import { Location } from '@angular/common';
import { AuthService } from 'src/app/services/auth.service';
import { Router } from '@angular/router';
import { BehaviorSubject, Observable, Subject, debounceTime, distinctUntilChanged, firstValueFrom, map, takeUntil } from 'rxjs'; 
import { Usuario } from 'src/app/models/Usuario';
import { UsuarioService } from 'src/app/services/usuario.service'; 
import { LoadingService } from 'src/app/services/loading.service';
import { UsuarioConCliente } from 'src/app/models/usuario-con-cliente';
import { ExcelService } from 'src/app/services/excel.service';
import { normalizeText } from 'src/app/shared/text.util';
import type * as XLSXType from 'xlsx-js-style';

@Component({
    selector: 'app-usuarios',
    templateUrl: './usuarios.component.html',
    styleUrls: ['./usuarios.component.css'],
    standalone: false
})
export class UsuariosComponent implements OnInit {
  private _authService = inject(AuthService);
  private _usuarioService = inject(UsuarioService);
  private _loadingService = inject(LoadingService);
  private _excelService = inject(ExcelService);
  private router = inject(Router);
  private _location = inject(Location);

  usuarios: UsuarioConCliente[] = [];
  usuariosFiltradas$?: Observable<UsuarioConCliente[]>;
  usuariosFiltradosActuales: UsuarioConCliente[] = [];
  searchTerm$ = new BehaviorSubject<string>('');
  error = signal<string>('');
  message = signal<string>('');
  private OnDestroy$ = new Subject();
  isExpanded = false;

  constructor(){

  }

  async ngOnInit(): Promise<void> {
    document.documentElement.scrollTop = 0;
    
    try {
      await this._loadingService.run(async () => {
        // 1) Sesión
        await this._authService.ensureSession();
        // 2) Datos
        const res = await firstValueFrom(this._usuarioService.getUsuariosXPerfil(2)); // 2 - CLIENTE
        this.usuarios = res ?? [];
        this.filterList?.();
      });
    } catch (e: any) {
      console.error(e);
      this.error = e?.message || 'No se pudo cargar tu cuenta.';
      this.router.navigate(['/sign-in']);
    }
  }

  filterList(): void {
    const collator = new Intl.Collator('es', {
      sensitivity: 'base',
      ignorePunctuation: true,
    });

    this.usuariosFiltradas$ = this.searchTerm$.pipe(
      debounceTime(200),
      distinctUntilChanged(),
      takeUntil(this.OnDestroy$),
      map(term => {
        const t = normalizeText(term);

        const filtrados = this.usuarios
          .filter(u => {
            const email = normalizeText(u.x_email);
            const nombre = normalizeText(u.cliente?.x_nombre);
            const apellido = normalizeText(u.cliente?.x_apellido);
            const documento = normalizeText(u.x_nro_documento);
            const telefono = normalizeText(u.cliente?.x_telefono);
            const obraSocial = normalizeText(u.cliente?.x_obra_social_plan);
            const nroSocio = normalizeText(u.x_nro_socio);
            const nombreCompleto = normalizeText(
              `${u.cliente?.x_nombre ?? ''} ${u.cliente?.x_apellido ?? ''}`
            );

            return (
              email.includes(t) ||
              nombre.includes(t) ||
              apellido.includes(t) ||
              nombreCompleto.includes(t) ||
              documento.includes(t) ||
              telefono.includes(t) ||
              obraSocial.includes(t) ||
              nroSocio.includes(t)
            );
          })
          .sort((a, b) => {
            const nombreA = `${a.cliente?.x_apellido ?? ''} ${a.cliente?.x_nombre ?? ''}`.trim();
            const nombreB = `${b.cliente?.x_apellido ?? ''} ${b.cliente?.x_nombre ?? ''}`.trim();

            return collator.compare(
              nombreA || a.x_email || '',
              nombreB || b.x_email || ''
            );
          });

        this.usuariosFiltradosActuales = filtrados;

        return filtrados;
      })
    );
  }

  /**
   * Activa/Desactiva un usuario.
   * Recibe el modelo y el nuevo estado (true/false).
   * Hace "optimistic update" y revierte si hay error.
   */
  async activarDesactivar(usuario: Usuario, checked: boolean): Promise<void> {
    // Guardá el valor previo antes de cambiarlo
    const prev = !!usuario.b_activo;

    // Optimistic UI: reflejar el cambio en la UI al toque
    usuario.b_activo = checked;

    try {
      await this._loadingService.run(async () => {
        await firstValueFrom(this._usuarioService.updateUsuarioFirebase(usuario));
      });
      this.message.set('Modificación realizada correctamente');
      setTimeout(() => this.message.set(''), 2500);
    } catch (e) {
      // Rollback si falla
      usuario.b_activo = prev;
      this.error.set('Error al realizar la modificación');
      console.error(e);
    }
  }

  toggleButton() {
    this.isExpanded = !this.isExpanded;
    const button = document.querySelector('.toggle-btn');
    if (this.isExpanded) {
      button?.classList.add('expanded');
    } else {
      button?.classList.remove('expanded');
    }
  }

  async exportarExcel(): Promise<void> {
    const xlsxMod = await import('xlsx-js-style');
    const XLSX = ((xlsxMod as any).default ?? xlsxMod) as any;

    const data = (
      this.usuariosFiltradosActuales.length > 0
        ? this.usuariosFiltradosActuales
        : this.usuarios
    ).map(u => ({
      Email: u.x_email ?? '',
      Nombre: u.cliente?.x_nombre ?? '',
      Apellido: u.cliente?.x_apellido ?? '',
      Teléfono: u.cliente?.x_telefono ?? '',
      Documento: u.x_nro_documento ?? '',
      'Obra social / Plan':
        u.cliente?.x_obra_social_plan ?? '',
      'Nro. socio': u.x_nro_socio ?? '',
      'Teléfono emergencia':
        u.x_telefono_emergencia ?? '',
      Activo: u.b_activo ? 'Sí' : 'No',
      'Fecha creación': u.d_fecha_creacion
        ? new Date(u.d_fecha_creacion)
            .toLocaleDateString('es-AR')
        : '',
    }));

    const worksheet: XLSXType.WorkSheet =
      XLSX.utils.json_to_sheet(data);

    // =========================
    // Headers con estilo
    // =========================
    const headers = [
      'A1','B1','C1','D1','E1',
      'F1','G1','H1','I1','J1'
    ];

    headers.forEach(cell => {
      if (!worksheet[cell]) return;

      worksheet[cell].s = {
        font: {
          bold: true,
          color: { rgb: 'FFFFFF' },
        },
        fill: {
          fgColor: { rgb: '102E47' },
        },
        alignment: {
          horizontal: 'center',
          vertical: 'center',
        },
      };
    });

    // =========================
    // Tamaño columnas
    // =========================
    worksheet['!cols'] = [
      { wch: 34 }, // Email
      { wch: 18 }, // Nombre
      { wch: 20 }, // Apellido
      { wch: 18 }, // Telefono
      { wch: 18 }, // Documento
      { wch: 28 }, // Obra social
      { wch: 18 }, // Nro socio
      { wch: 22 }, // Emergencia
      { wch: 12 }, // Activo
      { wch: 18 }, // Fecha
    ];

    // =========================
    // Autofiltro
    // =========================
    worksheet['!autofilter'] = {
      ref: `A1:J${data.length + 1}`,
    };

    const workbook = XLSX.utils.book_new();

    XLSX.utils.book_append_sheet(
      workbook,
      worksheet,
      'Usuarios'
    );

    await this._excelService.exportWorkbook(workbook, 'usuarios.xlsx', XLSX);
  }

  back(){
    this._location.back();
  }
}
